import React, { useEffect, useState, useRef } from 'react'
import { FormattedMessage } from 'react-intl';
import commonFunctions from '../../utils/commonFunctions';
import {format} from 'date-fns';
import API from '../../utils/API'
import Table from '../../components/Table/Table'
import LicenseModal from './LicenseModal'
import ReceiversModal from './ReceiversModal'
import ConfirmModal from '../../components/Modals/ConfirmModal'
import LicenseEulaModal from './LicenseEulaModal'
import RefreshingButton from '../../components/Buttons/RefreshingButton'
import tableCheckbox from '../../components/Inputs/tableCheckbox'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import constants from '../../utils/constants'
import trashIcon from '../../assets/icons/table/trash.svg'
import RefreshIcon from '../../assets/icons/common/refresh-ccw.svg'
import InputValidation from '../../utils/InputValidation'
import { fetchLicensesSuccess } from '../../store/licenses';
import useInfiniteScroll from '../../components/common/useInfiniteScroll'
import useWebSocket from '../../components/common/useWebSocket'

function LicenseManagement(props) {
  const navigate = useNavigate();
  const websocket = useWebSocket();
  const { closeModal } = commonFunctions;
  const { user } = props;
  const dateOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  const permissionIds = user.permissions.map((permission)=>{
    return permission.id
  })

  useEffect(() => {
    if(!permissionIds.includes(constants.permissionIds.licensesEdit)){
      navigate('/forbidden');
    };
  }, [navigate, permissionIds])

  const [licenses, setLicenses] = useState([]);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [cmsList, setCmsList] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingDelete, setRefreshingDelete] = useState(false);
  const [refreshingAdd, setRefreshingAdd] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activateError, setActivateError] = useState('');
  const [searchValue, setSearchValue] = useState('');  

  const [activateInfo, setActivateInfo] = useState('');
  const [butDisabled, setButDisabled] = useState('');
  const [disabledAccept, setDisabledAccept] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [modalData, setModalData] = useState([]);
  const [actionType, setActionType] = useState('');

  // Give state to all components of the dataMap(explained later) you want to render
  const [data, setData] = React.useState([]);
  const [dataCount, setDataCount] = React.useState(0);
  const [totalDataCount, setTotalDataCount] = React.useState(0);
  const [selectedIds, setSelectedIds] = React.useState({});
  const [sortByCol, setSortByCol] = React.useState('product_name');
  const [sortByAsc, setSortByAsc] = React.useState(true);
  const [startIndexForTable, setStartIndexForTable] = React.useState(0);

  const tableWrapperRef = useRef(null);
  const tableRef = useRef();
  const searchRef = useRef(null);
  const licenseReloadTimersRef = useRef([]);
  const lastLicenseReloadRef = useRef(0);
  const reloadLicenseTableAfterSyncRef = useRef(null);

  searchRef.current = searchValue;

  // Refresh licenses on page load
  useEffect(() => {
    refreshLicenses();
  }, []);

  /**
   * Load CMSes
   * @returns - Promise
   */
  async function loadCmses() {
    try {
      const { data } = await API.getCmses();
      setCmsList(data);
      return data;
    } catch (err) {
      console.error(err.message);
    }
  }

  function toNonNegativeInteger(value) {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.max(numericValue, 0) : null;
  }

  function getRemainingSeats(license) {
    const backendRemaining = toNonNegativeInteger(license.available_seats ?? license.availableSeats);
    if (backendRemaining !== null) {
      return backendRemaining;
    }

    const seats = toNonNegativeInteger(license.seats) ?? 0;
    const assignedCount = toNonNegativeInteger(license.assigned_count ?? license.assignedCount);
    if (assignedCount !== null) {
      return Math.max(seats - assignedCount, 0);
    }

    const cmses = Array.isArray(license.cmses) ? license.cmses : [];
    return cmses.reduce((prev, cms) => (
      prev - (Array.isArray(cms.receivers) ? cms.receivers.length : 0)
    ), seats);
  }
  
  /**
   * Load table with licenses
   */
  async function loadTable(map, resetSelection = true) {
    // Reset map
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }
  
    try {
      const cmsData = await loadCmses();
      const res = await API.getLicenses();
  
      res.data.forEach((license) => {
        let cmsName = '';
        let cmsId = '';
        const cmses = Array.isArray(license.cmses) ? license.cmses : [];

        // Masked version for display
        const activationKeyDisplay = license.activation_key.slice(0, 8) + '****';
  
        // Displayable string for features
        const featuresDisplay = license.feature_ids.join(', ');
  
        // Displayable string for remaining seats
        const remaining = getRemainingSeats(license);
        const remainingDisplay = remaining.toString(); // Convert to string for search
  
        // Displayable string for seats
        const seatsDisplay = license.seats.toString();
  
        // Displayable string for expiry date
        const expiryDisplay = license.expiry_date
          ? new Date(license.expiry_date).toLocaleString(undefined, dateOptions)
          : '';
  
        // Assign CMS details if a match is found in cmsData
        if (cmses.length > 0) {
          cmses.forEach((cms) => {
            cmsId = cms.cms_id;
            const cmsObj = cmsData.find(cmsObj => cmsObj.id === cms.cms_id);
            if (cmsObj) {
              cmsName = cmsObj.name;
            }
          });
        }
  
        // Add the license to map.data regardless of CMS
        map.data[license.product_id] = {
          product_id: license.product_id,
          product_name: license.product_name,
          activation_date: license.activation_date,
          activation_key: license.activation_key,
          activationKeyDisplay, // Masked version for display
          cmses,
          expiry_date: license.expiry_date,
          expiryDisplay,
          feature_ids: license.feature_ids,
          featuresDisplay, // String representation for search
          seats: license.seats,
          seatsDisplay, // String representation for search
          remaining,
          remainingDisplay, // String representation for search
          cmsName, // CMS name or empty string
          cmsId,   // CMS id or empty string
          checked: false,
        };
      });
  
      console.log('CMSes:', cmsData);
      console.log("Licenses:", map.data);  // Log map.data to ensure all licenses are added
  
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
  
      // Call React setState to render a section of our data
      if (searchRef.current !== "") {
        tableRef.current.search(map);
      } else {
        sortMap(map);
        setData(generateDataSlice(map));
        setDataCount(map.sortedIds.length);
      }
  
      setSelectedIds(map.selectedIds);
      setTotalDataCount(map.ids.length);
  
      const data = res.data.map((item) => {
        delete item.cmses;
        return item;
      });
      props.dispatch(fetchLicensesSuccess(data));
      setDataLoaded(true);
    } catch (err) {
      console.error('Error loading data:', err);
      setDataLoaded(true);
    }
  }

  const dataMap = React.useMemo(
    () => {
      const map = {data: {}, ids: [], selectedIds: {}, filteredIds: []};
      loadTable(map);
      return map;
    }
  , [])

  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100, 
    sortByCol,
    setSortByCol,
    sortByAsc, 
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
  });

  function clearScheduledLicenseReloads() {
    licenseReloadTimersRef.current.forEach((timer) => clearTimeout(timer));
    licenseReloadTimersRef.current = [];
  }

  async function requestCmsLicenseSeatSync() {
    try {
      await API.syncCmsLicenseSeats();
    } catch (err) {
      console.warn('Failed to request CMS license seat sync:', err.message);
    }
  }

  function reloadLicenseTableAfterSync({ requestSync = false } = {}) {
    clearScheduledLicenseReloads();
    if (requestSync) {
      requestCmsLicenseSeatSync();
    }
    loadTable(dataMap, false);
    [1000, 3000].forEach((delay) => {
      const timer = setTimeout(() => {
        loadTable(dataMap, false);
      }, delay);
      licenseReloadTimersRef.current.push(timer);
    });
  }
  reloadLicenseTableAfterSyncRef.current = reloadLicenseTableAfterSync;

  useEffect(() => {
    return () => {
      clearScheduledLicenseReloads();
    };
  }, []);

  useEffect(() => {
    if (!websocket) return;

    const handleLicenseSyncMessage = (message) => {
      try {
        const data = JSON.parse(message.data);
        if (data.messageId !== 5001) {
          return;
        }
        const now = Date.now();
        if (now - lastLicenseReloadRef.current < 2000) {
          return;
        }
        lastLicenseReloadRef.current = now;
        reloadLicenseTableAfterSyncRef.current?.();
      } catch (err) {
        console.error('Error processing license sync websocket message:', err);
      }
    };

    websocket.addEventListener('message', handleLicenseSyncMessage);
    return () => {
      websocket.removeEventListener('message', handleLicenseSyncMessage);
    };
  }, [websocket]);

  function refreshLicenses() {
    console.log('refreshing')
    setRefreshing(true);
    API.refreshLicenses() //Retrieve licenses from Soraco
      .then(({ data }) => {
        setLicenses(data);
        setSuccess(<FormattedMessage id="REFRESH_LICENSES_SUCCESS"/>)
      })
      .catch(err => {
        console.error(err.message);
        setError(<FormattedMessage id="REFRESH_LICENSES_FAILED" values={{info: <b>{err.message}</b>,}}/>)
      })
      .finally(()=>{
        setRefreshing(false);
        reloadLicenseTableAfterSync({ requestSync: true })
      })
  }

  /**
   * Handle click to delete a specific license.
   * @param {string} id 
   */
  function handleSingleDelete(e, id) {
    e.preventDefault();
    let clickedLicense = dataMap.data[id];
    console.log('clicked license:', clickedLicense);
    setSelectedLicense(clickedLicense);
    setActionType('single');
  }
  
  /**
   * Deletes Licenses
   * @param {Event} e 
   */
  function handleDeleteLicenses(e) {
    e.preventDefault();
    setRefreshingDelete(true);

    let licensesToDelete = [];

    if (actionType === 'single' && selectedLicense) {
      // Handle single license deletion
      licensesToDelete = [selectedLicense]; // Set the single license to delete
    } else {
      // Handle multiple license deletion
      licensesToDelete = Object.keys(dataMap.selectedIds)
        .filter(id => dataMap.selectedIds[id])
        .map(id => dataMap.data[id]);
    }

    console.log('Deleting licenses...', licensesToDelete);

    const promises = licensesToDelete.map((license) => {
      return API.deactivateLicense(license.activation_key)
        .then(() => {
          setSuccess(
            <FormattedMessage
              id="DEACTIVATE_LICENSE_KEY_SUCCESS"
              values={{ info: <b>{license.activation_key}</b> }}
            />
          );
          console.log(`Successfully deactivated license: ${license.activation_key}`);
        })
        .catch((err) => {
          console.error(err);
          setError(
            <FormattedMessage
              id="DEACTIVATE_LICENSE_KEY_FAILED"
              values={{ info: <b>{license.activation_key}</b> }}
            />
          );
          console.error(`Failed to deactivate license: ${license.activation_key}`);
        });
    });

    Promise.all(promises)
      .then(() => {
        // Refresh the licenses and update dataMap if necessary
        refreshLicenses();

        // Clear the selectedIds for the deleted licenses in dataMap
        const updatedSelectedIds = { ...dataMap.selectedIds };
        licensesToDelete.forEach((license) => {
          delete updatedSelectedIds[license.id];
        });

        // Update dataMap with the modified selectedIds
        dataMap.selectedIds = updatedSelectedIds;
      })
      .catch((err) => {
        console.error('Failed to deactivate licenses:', err.message);
        setError(
          <FormattedMessage
            id="DEACTIVATE_LICENSE_KEY_FAILED"
            values={{ info: <b>{err.message}</b> }}
          />
        );
      })
      .finally(() => {
        setRefreshingDelete(false);
      });

    closeModal('confirmModal');

    // Reset the action type and selected license after deletion
    setActionType('');
    setSelectedLicense(null);
  }

  function onRefreshClick(e) {
    e.preventDefault();
    setRefreshing(true);
    API.refreshLicenses()
    .then(({ data }) => {
      setLicenses(data);
      setSuccess(<FormattedMessage id="REFRESH_LICENSES_SUCCESS"/>)
    })
    .catch((err)=>{
      console.error(err);
      setError(<FormattedMessage id="REFRESH_LICENSES_FAILED" values={{info: <b>{err.message}</b>,}}/>)
    })
    .finally(()=>{
      setRefreshing(false);
      reloadLicenseTableAfterSync({ requestSync: true })
    })
  }

  function handleViewReceiversClick(cmses) {
    setModalData(cmses);
  }

  // Initialize refs for modal reset functions
  const resetLicenseModalRef = useRef(null);

  const handleResetLicenseModal = () => {
    if (resetLicenseModalRef.current) {
      resetLicenseModalRef.current();
    }
  };

  // Initialize table columns
  const tableColumnArray = [
    {
      Header: tableCheckbox,
      id: 'checkbox',
      accessor: 'checked',
      Cell: tableCheckbox,
      isVisible: true,
      width: 63,
      maxWidth: 63,
    },
    {
      Header: 'LICENSE',
      dataIndex: 'license',
      id: 'product_name',
      accessor: 'product_name',
      isVisible: true,
    },
    {
      Header: 'ACTIVATION_KEY',
      dataIndex: 'activation_key',
      id: 'activation_key',
      accessor: 'activationKeyDisplay',
      isVisible: true,
    },
    {
      Header: 'FEATURES',
      dataIndex: 'feature_ids',
      id: 'feature_ids',
      accessor: 'featuresDisplay',
      isVisible: true,
    },
    {
      Header: 'REMAINING',
      id: 'remaining',
      dataIndex: 'remaining',
      accessor: 'remainingDisplay', 
      isVisible: true,
    },
    {
      Header: 'SEATS',
      id: 'seats',
      dataIndex: 'seats',
      accessor: 'seatsDisplay', 
      isVisible: true,
    },
    {
      Header: 'EXPIRATION',
      id: 'expiry_date',
      dataIndex: 'expiry_date',
      accessor: 'expiryDisplay',
      isVisible: true,
    },
    {
      Header: '',
      id: 'viewReceivers',
      Cell: ({ row }) => (
        row.original.cmses && row.original.cmses.length > 0 ? (
          <div
            type="button"
            className="link-button"
            data-bs-toggle="modal" 
            data-bs-target="#receiversModal"
            onClick={() => handleViewReceiversClick(row.original.cmses)}
          >
            <FormattedMessage id="VIEW_RECEIVERS" />
          </div>
        ) : null
      ),
      isVisible: true,
    },
    {
      Header: '',
      id: 'actions',
      isVisible: true,
      width: 100, 
      maxWidth: 100,
      Cell: ({ row }) => (
        <div className="actions">
           <button
            className="btn"
            data-bs-toggle="modal"
            data-bs-target="#confirmModal"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              if (handleSingleDelete) handleSingleDelete(e, row.original.id);
            }}
          >
            <img src={trashIcon} className="actionIcon deleteAction" alt={<FormattedMessage id="DEACTIVATE" />} />
          </button>
        </div>
      ),
    }
  ]

  const tableColumns = React.useMemo(() => tableColumnArray, []);

  const subTableColumns = React.useMemo(
    () => [
      {
        Header: 'CMS',
        dataIndex: 'cmsName',
        accessor: (cms) => {
          let text = '';
          cmsList.forEach((cmsObj) => {
            console.log(cms.cms_id, cmsObj.id, cms.cms_id === cmsObj.id)
            if(cms.cms_id === cmsObj.id) {
              text = cmsObj.name;
            }
          })
          return text;
        },
        isVisible: true,
      },
      {
        Header: 'ID',
        dataIndex: 'cmsId',
        accessor: (cms) => {
          return cms.cms_id;
        },
        isVisible: true,
      }, 
      {
        Header: 'RECEIVERS',
        dataIndex: 'receivers',
        accessor: 'receivers',
        isVisible: true,
      }, 
    ]
  )

  const buttons = {
    toolbarLeftButtons: (
      <>
        <RefreshingButton
          id="refreshLicensesBtn"
          onClick={onRefreshClick}
          hidden={!onRefreshClick && true}
          refreshing={refreshing}
          buttonStyle={"btn btn-left"}
        >
          <img src={RefreshIcon} alt="refresh" style={{ marginRight: '8px' }}/>
          <FormattedMessage id="REFRESH" defaultMessage="Refresh" />
        </RefreshingButton>
      </>
    ),
    toolbarRightButtons: (
      <>
        <RefreshingButton
          id="activateLicenseBtn"
          modalTarget={`#licenseEulaModal`}
          onClick={() => {
            setDisabledAccept(true); 
            handleResetLicenseModal();
          }}
          refreshing={refreshingAdd}
          buttonStyle="btn btn-filled"
        >
          <FormattedMessage id="ACTIVATE_LICENSE" defaultMessage="Activate" />
        </RefreshingButton>
      </>
    ),
    footerButtons: (
      <>
        <RefreshingButton
          id="deactivateLicenseBtn"
          buttonStyle="btn deleteBtn"
          onClick={handleDeleteLicenses}
          disabled={Object.keys(selectedIds).length === 0}
          refreshing={refreshingDelete}
        >
          <FormattedMessage id="DEACTIVATE" defaultMessage="Deactivate" />
        </RefreshingButton>
      </>
    )
  }

  // Props to pass to our license table 
  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "licenses_table",
    pageType: constants.pageType.Licenses,
    modalId: null,
    expandingRows: cmsList.length,
    columns: tableColumns,
    // Data props
    data, setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount, setDataCount,
    totalDataCount,
    // Sorting props
    setSortBy,
    sortByCol,
    sortByAsc,
    // Error Handling
    success, setSuccess,
    error, setError,
    // Common table props
    startIndex: startIndexForTable,
    selectedIds, setSelectedIds,
    searchValue, 
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    // Action column props
    handleSingleDelete,
    buttons,
  }

  const subTableProps = {
    columns: subTableColumns,
    data,
    startMargin: 80,
  }
  
  // Props to pass to our modal component
  const modalProps = {
    setResetLicenseModalRef: (resetFunc) => { resetLicenseModalRef.current = resetFunc; },
    activateError, setActivateError, 
    success, setSuccess,
    error, setError,
    closeModal,
    activateInfo, setActivateInfo,
    butDisabled, setButDisabled,
    setRefreshingAdd,
    refreshLicenses,
  }

  const confirmModalProps = {
    actionType,
    selectedItem: selectedLicense,
    checkedItems: Object.keys(selectedIds),
    handleDelete: handleDeleteLicenses,
    closeModal,
    dataType: 'license',
  }

  // Props to pass to our modal component
  const modalEulaProps = { 
    disabledAccept,
    setDisabledAccept,
    closeModal
  }

  return (
    <div>
      <LicenseModal {...modalProps} />
      <ReceiversModal 
        cmses={modalData} 
        cmsList={cmsList} 
        onClose={() => setModalData([])} 
      />
      <ConfirmModal {...confirmModalProps} />
      <LicenseEulaModal {...modalEulaProps}/>
      {/* Only display full table if user is account owner */}
      <Table {...tableProps} subTableProps = {subTableProps} /> 
    </div>
  )
}

const mapStateToProps = (state) => ({
  user: state.user
})

export default connect(mapStateToProps)(LicenseManagement)
